import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Briefcase, BookOpen, Award, History, AlertTriangle, Star, Search, ChevronRight, FileText} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

function ColaboradorDashboard() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [profileData, setProfileData] = useState<any>(null)
  const [profileScore, setProfileScore] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    const fetchData = async () => {
      try {
        if (user) {
          const res = await lumi.entities.collaborators.list({ filter: { userId: user.userId }, limit: 1 })
          if (res.list.length > 0) {
            setProfileData(res.list[0])
            setProfileScore(calculateScore(res.list[0]))
          }
        }
      } catch (e) {
        console.error('Error al cargar datos colaborador:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [isAuthenticated, user])

  const calculateScore = (data: any): number => {
    const fields = ['fullName', 'phone', 'address', 'city', 'state', 'birthDate', 'education', 'experience', 'skills', 'photoUrl', 'cvUrl', 'salaryExpectation']
    const filled = fields.filter((f) => {
      const v = data[f]
      return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
    })
    return Math.round((filled.length / fields.length) * 100)
  }

  const scoreBar =
    profileScore >= 80 ? 'bg-emerald-500' : profileScore >= 50 ? 'bg-amber-500' : 'bg-red-500'
  const scoreText =
    profileScore >= 80 ? 'text-emerald-600' : profileScore >= 50 ? 'text-amber-600' : 'text-red-600'

  return (
    <ColaboradorLayout
      title={`Hola${profileData?.fullName ? `, ${profileData.fullName.split(' ')[0]}` : ''} 👋`}
      subtitle="Tu espacio profesional en Beeznz Work"
    >
      {/* Completitud de perfil */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Completitud de perfil</p>
            <p className="text-xs text-gray-500">Un perfil completo atrae mejores oportunidades</p>
          </div>
          <span className={`text-3xl font-bold ${scoreText}`}>{loading ? '—' : `${profileScore}%`}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full transition-all duration-700 ${scoreBar}`}
            style={{ width: `${profileScore}%` }}
          />
        </div>
        {profileScore < 100 && (
          <Link to="/colaborador/perfil" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:underline">
            Completar perfil <ChevronRight size={13} />
          </Link>
        )}
      </div>

      {/* Acciones rápidas */}
      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Acciones rápidas</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Buscar empleos', desc: 'Explora vacantes disponibles', to: '/colaborador/empleos', icon: Search },
            { label: 'Mis postulaciones', desc: 'Revisa el estado de tus postulaciones', to: '/colaborador/postulaciones', icon: Briefcase },
            { label: 'Historial laboral', desc: 'Ve tu historial verificable', to: '/colaborador/historial', icon: History },
            { label: 'Capacitaciones', desc: 'Cursos y certificaciones', to: '/colaborador/capacitaciones', icon: BookOpen },
            { label: 'Mis evaluaciones', desc: 'Calificaciones de tus empleadores', to: '/colaborador/evaluaciones', icon: Star },
            { label: 'Generar reporte', desc: 'Reporta una conducta laboral', to: '/colaborador/reportes', icon: AlertTriangle },
          ].map(({ label, desc, to, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-amber-300 hover:shadow"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                <Icon size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 truncate">{desc}</p>
              </div>
              <ChevronRight size={15} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
            </Link>
          ))}
        </div>
      </div>

      {/* Certificados rápidos */}
      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Tu desarrollo</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Link
            to="/colaborador/certificados"
            className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-emerald-300 hover:shadow"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
              <Award size={18} className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Mis certificados</p>
              <p className="text-xs text-gray-500">Certificaciones obtenidas</p>
            </div>
            <ChevronRight size={15} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
          </Link>
        </div>
      </div>

      {/* Onboarding */}
      {!loading && !profileData && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <FileText size={20} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Crea tu perfil profesional</p>
              <p className="mt-1 text-sm text-amber-700">
                Completa tu perfil para aparecer en búsquedas de empresas y postularte a vacantes.
              </p>
              <Link
                to="/colaborador/perfil"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
              >
                Crear perfil <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorDashboard
